import React, { Fragment, useState } from "react";
import { SzButton, SzCheckbox, SzInput, SzModal } from "@suezenv/react-theme-components";
import { useTranslationWithPrefix } from "../../../hooks/translation";
import { ISignalCustomParams, SignalChangeActionType, StatusChangeActionType } from "../../../type/signal.type";
import { CURRENT_ACTION_IS_APPROVED } from "../../../constants/services";
import "./action-modal.scss";
import FileUploader from "../../file-uploader/file-uploader";
import { fileWhitelist } from "../../../form/json-form/field/FileInputDragAndDrop";
import { convertFilesToBase64 } from "../../../helpers/file.util";

export interface IActionModal {
    action: StatusChangeActionType|SignalChangeActionType;
    show: boolean;
    setShow: (flag: boolean) => void;
    validate: (
        action: StatusChangeActionType|SignalChangeActionType,
        data: IValidationActionAttachments|IValidationActionComment
    ) => void;
    customParams?: ISignalCustomParams;
    customAction?: () => void;
}

export interface IValidationActionAttachments {
    filesAsBase64: string[],
}

export interface IValidationActionComment {
    comment: string,
}

const ActionModal: React.FC<IActionModal> = ({ show, action, setShow, validate, customParams, customAction = () => {} }) => {
    const trans = useTranslationWithPrefix("signalement", "detail_signal.modal");
    const [checked, setChecked] = useState<string>("");
    const [newAttachments, setNewAttachments] = useState<string[]>([]);
    const [comment, setComment] = useState("");

    const closeModal = (): void => {
        setShow(false);
        setComment("");
    }

    const onValidateClicked = (): void => {
        let data: IValidationActionComment|IValidationActionAttachments;

        switch (action) {
            case 'attachments':
                data = { filesAsBase64: newAttachments };
            break;
            default:
                data = { comment: checked ? trans(`${action}.${checked}`) : comment };
        }

        validate(action, data)
        closeModal();
    }

    const onCancelClicked = (): void => {
        if (checked) {
            setChecked("");
        }

        closeModal();
    }

    const onCheckBoxChange = (e: any): void => {
        if (e.target.name === checked) {
            setChecked("");
        } else {
            setChecked(e.target.name);
            setComment("");
        }
    }

    const onChangeInput = (e: any): void => {
        if (checked) {
            setChecked("");
        }

        setComment(e.target.value);
    }

    const renderAction = (): JSX.Element => {
        const commentField = (
            <SzInput
                className="mb-5"
                name="input"
                value={comment}
                onChange={onChangeInput}
                label={trans(`${action}.input_label`)}
                placeholder={trans(`input_placeholder`)}
                required={action === "comment"}
            />
        );

        switch (action) {
            case 'rejected':
                return (
                    <Fragment>
                        <div className="row">
                            <div className="col">
                                <SzCheckbox
                                    id="radio"
                                    className="mb-2"
                                    name="raison_1"
                                    label={trans(`${action}.raison_1`)}
                                    type="radio"
                                    checked={checked === "raison_1"}
                                    onChange={onCheckBoxChange}
                                />
                                <SzCheckbox
                                    id="radio-2"
                                    className="mb-2"
                                    name="raison_2"
                                    label={trans(`${action}.raison_2`)}
                                    type="radio"
                                    checked={checked === "raison_2"}
                                    onChange={onCheckBoxChange}
                                />
                                <SzCheckbox
                                    id="radio-3"
                                    className="mb-2"
                                    name="raison_3"
                                    label={trans(`${action}.raison_3`)}
                                    type="radio"
                                    checked={checked === "raison_3"}
                                    onChange={onCheckBoxChange}
                                />
                            </div>
                        </div>
                        {commentField}
                    </Fragment>
                );
            case 'attachments':
                return <FileUploader
                    dragAndDropText={''}
                    canDeleteFile={true}
                    allowedExtensions={fileWhitelist}
                    onChange={async (files: File[]) => {
                        setNewAttachments(await convertFilesToBase64(files));
                    }}/>
            default:
                return commentField;
        }
    };

    return (
        <SzModal
            className="action-modal"
            centered={true}
            title={trans(`${action}.title`)}
            show={show}
            handleClose={onCancelClicked}
            size={"xl"}
        >
            <div className="row justify-content-around">
                <div className="col-8">
                    {renderAction()}
                    <div className="row justify-content-between mt-1">
                        <div className="col-auto">
                            <SzButton variant="secondary" className="mr-3" icon="remove" onClick={onCancelClicked}>
                                {trans(`cancel_btn`)}
                            </SzButton>
                        </div>
                        {
                        customParams?.isDisplayCreateRequestAndCloseButton &&  (CURRENT_ACTION_IS_APPROVED === action) && (<div className="col-auto">
                            <SzButton
                                className="mr-3"
                                icon="messages-bubble-edit"
                                onClick={() => {
                                    customAction()
                                    closeModal()
                                    // Don't change status now, only when request creation is done
                                }}
                            >
                                {trans('customaction_btn')}
                            </SzButton>
                        </div>)
                        }
                        <div className="col-auto">
                            <SzButton
                                className="mr-3"
                                icon="messages-bubble-edit"
                                isDisabled={!comment && action === "comment"}
                                onClick={onValidateClicked}
                            >
                                {trans(`valide_btn`)}
                            </SzButton>
                        </div>
                    </div>
                </div>
            </div>
        </SzModal>
    );
};

export default ActionModal;
